import { useState, useRef, useCallback } from 'react';
import conversationService from '../services/conversationService';

const useConversation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const streamCleanupRef = useRef(null);
  const pendingRequestsRef = useRef(new Map());

  const getConversations = async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await conversationService.getConversations(projectId);
      if (Array.isArray(data)) {
        setConversations(data);
      } else {
        setConversations([]);
        console.warn("getConversations returned non-array data:", data);
      }
      return data || [];
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError(err.message || 'Failed to fetch conversations');
      // Return empty array instead of throwing to avoid breaking the UI
      setConversations([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getConversation = async (conversationId) => {
    // Prevent duplicate requests for the same conversation
    if (pendingRequestsRef.current.has(conversationId)) {
      return pendingRequestsRef.current.get(conversationId);
    }

    const requestPromise = (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await conversationService.getConversation(conversationId);
      if (data && data._id) {
        // Deduplication is now handled in conversationService
        setConversation(data);
        return data;
      } else {
        console.warn("Retrieved conversation data appears invalid:", data);
        return null;
      }
      } catch (err) {
        console.error(`Error fetching conversation ${conversationId}:`, err);
        setError(err.message || 'Failed to fetch conversation');
        return null;
      } finally {
        setLoading(false);
        pendingRequestsRef.current.delete(conversationId);
      }
    })();

    pendingRequestsRef.current.set(conversationId, requestPromise);
    return requestPromise;
  };

  /**
   * Create a new conversation
   * @param {string} projectId - Project ID
   * @param {string} title - Conversation title
   * @param {string} taskId - Optional task ID for task-specific conversations
   * @returns {Promise<Object>} Created conversation
   */
  const createConversation = async (projectId, title, taskId = null) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Creating new conversation for project ${projectId}${taskId ? ` with task ${taskId}` : ''}`);
      
      // Create the request data object
      const conversationData = {
        title: title
      };
      
      // Add task_id to the request data if provided
      if (taskId) {
        conversationData.task_id = taskId;
      }
      
      const data = await conversationService.createConversation(projectId, conversationData);
      
      if (data && data._id) {
        console.log(`Successfully created conversation: ${data._id}`);
        setConversation(data);
        return data;
      } else {
        console.warn("Created conversation data appears invalid:", data);
        throw new Error('Invalid conversation data received from server');
      }
    } catch (err) {
      console.error(`Error creating conversation for project ${projectId}:`, err);
      setError(err.message || 'Failed to create conversation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addMessage = async (conversationId, content, role = 'user', attachments = []) => {
    try {
      setLoading(true);
      setError(null);
      const data = await conversationService.addMessage(conversationId, content, role, attachments);
      setConversation(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to add message');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addArtifact = async (conversationId, type, title, content, metadata = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await conversationService.addArtifact(conversationId, type, title, content, metadata);
      
      // Update the local conversation state with the new artifact
      setConversation(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          artifacts: [...prev.artifacts, data]
        };
      });
      
      return data;
    } catch (err) {
      setError(err.message || 'Failed to add artifact');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const saveConversation = async (conversationId, conversationData = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`useConversation - Saving conversation ${conversationId}`);
      
      // Validate conversation ID before proceeding
      if (!conversationId || conversationId.startsWith('temp-')) {
        const errMsg = 'Cannot save temporary conversation - need a real conversation ID';
        console.error(errMsg);
        setError(errMsg);
        throw new Error(errMsg);
      }
      
      // Ensure we have at least the basic required fields
      const completeData = {
        ...conversationData,
        // Add any missing required fields with defaults
        title: conversationData.title || `Conversation ${new Date().toLocaleString()}`,
        // Make sure we have projectId if it wasn't provided
        projectId: conversationData.projectId || conversation?.projectId
      };
      
      console.log(`Sending save request with data:`, completeData);
      
      const data = await conversationService.saveConversation(conversationId, completeData);
      
      // Only update state if we got a valid response
      if (data && data._id) {
        console.log(`Successfully saved conversation:`, data);
        setConversation(data);
        return data;
      } else {
        const errMsg = 'Received invalid data from save operation';
        console.error(errMsg, data);
        setError(errMsg);
        throw new Error(errMsg);
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to save conversation';
      console.error(`Save conversation error for ${conversationId}:`, errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const generateDocument = async (conversationId, taskId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await conversationService.generateDocument(conversationId, taskId);
      return data;
    } catch (err) {
      console.error('Error in generateDocument hook:', err);
      setError(err.message || err || 'Failed to generate document');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Stream a message, receiving real-time updates from the server
  const streamMessage = async (conversationId, content) => {
    try {
      // Clean up any previous stream
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
        streamCleanupRef.current = null;
      }
      
      // Reset streaming state
      setStreamingContent('');
      setIsStreaming(true);
      setError(null);
      
      // Add user message to local state immediately for better UX
      const userMessage = {
        role: 'user',
        content: content,
        createdAt: new Date().toISOString()
      };
      
      // Update conversation with the user message
      setConversation(prev => {
        if (!prev) return prev;
        
        // Check if the last message is already this user message (edit case)
        const messages = prev.messages || [];
        const lastMessage = messages[messages.length - 1];
        
        if (lastMessage && lastMessage.role === 'user' && lastMessage.content === content) {
          // Message already exists (from edit), don't add duplicate
          return prev;
        }
        
        return {
          ...prev,
          messages: [...messages, userMessage]
        };
      });
      
      // Setup cleanup function
      const abortController = new AbortController();
      streamCleanupRef.current = () => {
        abortController.abort();
        setIsStreaming(false);
      };
      
      // Cache the conversation ID to use in callbacks
      // This prevents issues with stale closure references
      const activeConversationId = conversationId;
      
      // Used to track if this is a fallback/simulated stream
      let isFallbackStreaming = false;
      
      // Start streaming from the server - returns immediately with abort function
      const streamResult = conversationService.streamMessage(
        activeConversationId,
        content,
        // On chunk received
        (chunk, fullText) => {
          console.log(`Stream chunk received: ${chunk.length} chars, total: ${fullText.length} chars`);
          
          // Immediately update streaming content for real-time display
          setStreamingContent(fullText);
          
          // If we get a large chunk all at once, likely using fallback (non-streaming) API
          if (chunk.length > 100) {
            console.log('Detected large chunk, likely using fallback streaming');
            isFallbackStreaming = true;
          }
        },
        // On complete
        (finalText) => {
          console.log(`Stream complete, final text: ${finalText.length} chars`);
          setIsStreaming(false);
          
          // Add assistant message to conversation once streaming is complete
          const assistantMessage = {
            role: 'assistant',
            content: finalText,
            createdAt: new Date().toISOString()
          };
          
          setConversation(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...(prev.messages || []), assistantMessage]
            };
          });
          
          // If we're using the real streaming endpoint (not fallback),
          // refresh the conversation from the server to ensure we have the latest data
          if (!isFallbackStreaming) {
            console.log("Using real streaming endpoint, refreshing conversation");
            getConversation(activeConversationId).catch(err => {
              console.error("Failed to refresh conversation after streaming:", err);
            });
          } else {
            console.log("Using fallback streaming, no need to refresh conversation");
          }
          
          // Clear streaming content
          setStreamingContent('');
          streamCleanupRef.current = null;
        },
        // On error
        (err) => {
          console.error("Stream error:", err);
          setError(err.message || "Failed to stream message");
          setIsStreaming(false);
          streamCleanupRef.current = null;
          
          // Try to refresh the conversation on error to ensure we have the latest data
          getConversation(activeConversationId).catch(refreshErr => {
            console.error("Failed to refresh conversation after streaming error:", refreshErr);
          });
        }
      );
      
      // Store the abort function for later use
      streamCleanupRef.current = () => {
        console.log('Calling abort from useConversation cleanup');
        streamResult.abort();
        setIsStreaming(false);
      };
      
      // Wait for the streaming to complete
      await streamResult.promise;
    } catch (err) {
      console.error("Failed to start streaming:", err);
      setError(err.message || 'Failed to stream message');
      setIsStreaming(false);
      streamCleanupRef.current = null;
    }
  };
  
  // Cancel an ongoing stream - memoized to prevent unnecessary re-renders
  const cancelStream = useCallback(() => {
    console.log('cancelStream called in useConversation');
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
      streamCleanupRef.current = null;
      setIsStreaming(false);
    }
  }, []);

  // Upload file attachments to a conversation
  const uploadFiles = async (conversationId, files, message) => {
    try {
      setLoading(true);
      setError(null);
      const response = await conversationService.uploadFiles(conversationId, files, message);
      
      // If the response includes the full conversation, update our state
      if (response?.conversation) {
        setConversation(response.conversation);
        console.log("Updated conversation state with server data after file upload");
      }
      
      return response;
    } catch (err) {
      console.error("Error in uploadFiles hook:", err);
      setError(err.message || 'Failed to upload files');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    conversation,
    conversations,
    isStreaming,
    streamingContent,
    getConversations,
    getConversation,
    createConversation,
    addMessage,
    streamMessage,
    cancelStream,
    addArtifact,
    saveConversation,
    generateDocument,
    uploadFiles,
    setConversation
  };
};

export default useConversation;